import React from "react";
import { usePlayer } from "@/context/PlayerContext";
import { Play, Pause, SkipBack, SkipForward, Volume2 } from "lucide-react";

function fmt(s) {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function AudioPlayer() {
  const { current, playing, progress, duration, volume, setVolume, togglePlay, seek, playNext, playPrev } = usePlayer();
  if (!current) return null;
  const pct = duration ? (progress / duration) * 100 : 0;

  return (
    <div
      data-testid="audio-player"
      className="fixed bottom-0 left-0 right-0 h-24 backdrop-blur-xl bg-black/85 border-t border-[#2a2a2a] px-4 sm:px-6 flex items-center justify-between z-50"
    >
      <div className="flex items-center gap-3 w-56 sm:w-72 min-w-0">
        <div className="w-12 h-12 bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center shrink-0 overflow-hidden">
          {current.cover_url ? (
            <img src={current.cover_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="flex">
              <span className="sound-bar" /><span className="sound-bar" /><span className="sound-bar" /><span className="sound-bar" />
            </div>
          )}
        </div>
        <div className="min-w-0">
          <div className="font-display text-sm truncate" data-testid="player-title">{current.title}</div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#a3a3a3] truncate">{current.artist}</div>
        </div>
      </div>

      <div className="flex-1 max-w-2xl mx-3 sm:mx-6 flex flex-col gap-2">
        <div className="flex items-center justify-center gap-4">
          <button onClick={playPrev} data-testid="player-prev" className="text-[#a3a3a3] hover:text-[#f5f5f5] transition-colors">
            <SkipBack className="w-4 h-4" />
          </button>
          <button
            onClick={togglePlay}
            data-testid="player-toggle"
            className="w-9 h-9 bg-[#ff4500] hover:bg-[#ff5722] text-white flex items-center justify-center transition-colors active:scale-95"
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </button>
          <button onClick={playNext} data-testid="player-next" className="text-[#a3a3a3] hover:text-[#f5f5f5] transition-colors">
            <SkipForward className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono text-[#a3a3a3] w-10 text-right">{fmt(progress)}</span>
          <div
            className="flex-1 h-1 bg-[#2a2a2a] cursor-pointer relative"
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              const x = (e.clientX - r.left) / r.width;
              seek(x * duration);
            }}
            data-testid="player-progress"
          >
            <div className="absolute inset-y-0 left-0 bg-[#ff4500]" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[10px] font-mono text-[#a3a3a3] w-10">{fmt(duration)}</span>
        </div>
      </div>

      <div className="hidden sm:flex items-center gap-2 w-32">
        <Volume2 className="w-4 h-4 text-[#a3a3a3]" />
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={(e) => setVolume(parseFloat(e.target.value))}
          data-testid="player-volume"
          className="w-full accent-[#ff4500]"
        />
      </div>
    </div>
  );
}
